import axios, { AxiosInstance } from 'axios'
import { create } from 'zustand'
import {
  HookResult,
  RequestParameters,
  createDefaultHookResult,
} from '../hooks/hookTypes'

// Configuration
const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4040' // Default to localhost if not set

let axiosInstance: AxiosInstance | null = null

function createAxiosInstance(): AxiosInstance {
  return axios.create({
    baseURL: baseURL,
    timeout: 30000, // 30 seconds timeout
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Allow credentials (session/cookies) to be sent
  })
}

export function getAxiosInstance(): AxiosInstance {
  if (!axiosInstance) {
    axiosInstance = createAxiosInstance()
  }
  return axiosInstance
}

// Generic axios request function
export const axiosRequest = (
  { method, url }: { method: string; url: string },
  options?: any
) => {
  return create<HookResult>((set) => ({
    data: null,
    loading: false,
    success: false,
    error: null,
    errorData: null,
    execute: (parameters: RequestParameters = {}) => {
      const { id, data, params, force } = parameters

      // Handle URL parameter substitution (e.g., :id, :key, :versionNumber, etc.)
      let finalUrl = url

      // Replace any URL placeholders with their values from parameters
      if (parameters) {
        Object.entries(parameters).forEach(([key, value]) => {
          // Skip non-string/non-number values or standard parameters like data, params, onSuccess, etc.
          if (
            (typeof value === 'string' || typeof value === 'number') &&
            ![
              'data',
              'params',
              'force',
              'onSuccess',
              'onError',
              'onFinal',
            ].includes(key)
          ) {
            const placeholder = `:${key}`
            if (finalUrl.includes(placeholder)) {
              finalUrl = finalUrl.replace(
                placeholder,
                encodeURIComponent(String(value))
              )
            }
          }
        })
      }

      // For backward compatibility - if :id is not replaced and id is provided
      if (finalUrl.includes(':id') && id) {
        finalUrl = finalUrl.replace(':id', encodeURIComponent(id))
      }

      // Only append id to the end if:
      // 1. ID parameter is provided
      // 2. URL doesn't contain any placeholders (like :id, :key, etc.)
      // 3. URL doesn't already end with this ID
      // 4. URL doesn't match the pattern of MongoDB ID at the end
      if (
        id &&
        !finalUrl.includes(':') &&
        !finalUrl.endsWith('/' + encodeURIComponent(id)) &&
        !(/\/[a-f0-9]{24}$/i.exec(finalUrl)) &&
        finalUrl.endsWith('/')
      ) {
        finalUrl = finalUrl + encodeURIComponent(id)
      }

      set({
        data: null,
        loading: true,
        success: false,
        error: null,
        errorData: null,
      })

      getAxiosInstance()({
        method,
        url: finalUrl,
        data,
        params,
      })
        .then((res) => {
          set({
            loading: false,
            success: true,
            data: res.data,
            error: null,
            errorData: null,
          })
          options?.onSuccess?.(res)
          parameters.onSuccess?.(res)
        })
        .catch((err) => {
          set({
            loading: false,
            success: false,
            data: null,
            error: err.message || 'An error occurred',
            errorData: err.response?.data || err,
          })
          options?.onError?.(err)
          parameters.onError?.(err)
        })
        .finally(() => {
          options?.onFinal?.()
          parameters.onFinal?.()
        })
    },
  }))
}

// Logout request
export const useLogout = axiosRequest({
  method: 'POST',
  url: '/logout',
})

// Response interceptor
getAxiosInstance().interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response?.data?.redirectTo) {
      console.log('Redirecting to:', error.response.data.redirectTo)
      window.location.href = error.response.data.redirectTo
    }
    if (error.response && error.response.status === 401) {
      console.log('Unauthorized, redirecting...', error)
      // Handle unauthorized access (e.g., redirect to login page)
    }
    return Promise.reject(error)
  }
)

export default axiosInstance
